import React, { useMemo, useState } from "react";
import { baseURL } from "../../Constants";
import styles from "./ApiDocsPage.module.css";

// Public, read-only API surface. This page documents ONLY /api/public/v1 and lets
// consumers try each endpoint live with their own key. It is intentionally public
// (no login) so casters can read the docs before requesting a key.
const API_BASE = `${baseURL}/public/v1`;

type Param = {
    name: string;
    in: "path" | "query";
    required?: boolean;
    default?: string;
    hint?: string;
};

type Endpoint = {
    id: string;
    method: "GET";
    path: string; // template, e.g. "/players/{steamId}"
    title: string;
    description: string;
    params: Param[];
};

type Group = { name: string; blurb: string; endpoints: Endpoint[] };

// Prefilled "try it" values, kept in ONE place so they can't drift apart per endpoint.
// They deliberately all describe the SAME player and team, so you can trace one subject
// through every endpoint on the page.
//
// Every value here was checked against production for CURRENT_SEASON — a default that 404s
// is worse than no default, and that's exactly what happened before: the old steamId was a
// real player who simply hadn't played in the new season yet, so the very first example on
// the page returned "No player found".
//
// When rolling to a new season, re-check these: a player only appears once their team has
// played, so early in a season pick someone from the qualifiers.
const CURRENT_SEASON = "33";
const EXAMPLE = {
    steamId: "76561198026827950",
    playerHint: "Ryxxo (Linx Legacy)",
    teamId: "64f1cdcd5c669b93700096ed",
    teamHint: "Linx Legacy",
    // A played match — has scores, veto and clips.
    matchId: "6a8dd52429428fe9b4803ac9",
    matchHint: "a played Linx Legacy match",
    // An upcoming fixture id, for the prematch reel. Still works after it's been played.
    fixtureId: "6a9af82d88b8dc0c3db443c2",
};

const GROUPS: Group[] = [
    {
        name: "Players",
        blurb:
            "Per-season player stats, including Rating 3.0, Round Swing % and eco-adjusted ADR/KAST (rating30, swingPct, eAdr, eKast — null before season 32, since older seasons were never parsed with the 3.0 engine). rating stays Rating 2.1. Names are the in-game player name only — never real names.",
        endpoints: [
            {
                id: "player-get",
                method: "GET",
                path: "/players/{steamId}",
                title: "Single player",
                description: "A player's stats for a season, by Steam ID.",
                params: [
                    { name: "steamId", in: "path", required: true, default: EXAMPLE.steamId, hint: EXAMPLE.playerHint },
                    { name: "season", in: "query", default: CURRENT_SEASON, hint: "defaults to current season" },
                ],
            },
            {
                id: "player-list",
                method: "GET",
                path: "/players",
                title: "Player list (paginated)",
                description:
                    "A season's active players ordered by rating, or a team's roster if you pass teamId. Paginated — never an unbounded dump.",
                params: [
                    { name: "season", in: "query", default: CURRENT_SEASON },
                    { name: "teamId", in: "query", hint: "optional — returns that team's roster" },
                    { name: "page", in: "query", default: "1" },
                    { name: "pageSize", in: "query", default: "5", hint: "max 100" },
                ],
            },
        ],
    },
    {
        name: "Teams",
        blurb: "Teams and rosters for a season.",
        endpoints: [
            {
                id: "teams-list",
                method: "GET",
                path: "/teams",
                title: "Teams in a season",
                description: "All teams in a season, optionally filtered by division.",
                params: [
                    { name: "season", in: "query", default: CURRENT_SEASON },
                    { name: "division", in: "query", hint: 'e.g. "ligaen", "1div", "2divA"' },
                ],
            },
            {
                id: "team-get",
                method: "GET",
                path: "/teams/{teamId}",
                title: "Single team",
                description: "One team by its ID.",
                params: [
                    { name: "teamId", in: "path", required: true, default: EXAMPLE.teamId, hint: EXAMPLE.teamHint },
                    { name: "season", in: "query", default: CURRENT_SEASON },
                ],
            },
            {
                id: "team-roster",
                method: "GET",
                path: "/teams/{teamId}/players",
                title: "Team roster",
                description: "A team's roster with each player's season stats.",
                params: [
                    { name: "teamId", in: "path", required: true, default: EXAMPLE.teamId, hint: EXAMPLE.teamHint },
                    { name: "season", in: "query", default: CURRENT_SEASON },
                ],
            },
        ],
    },
    {
        name: "Standings",
        blurb: "Division standings, keyed by division name.",
        endpoints: [
            {
                id: "standings",
                method: "GET",
                path: "/standings",
                title: "Division standings",
                description:
                    'Standings for a season, keyed by division ("ligaen", "1div", "2divA", …). Pass majorDivision to narrow to one group.',
                params: [
                    { name: "season", in: "query", default: CURRENT_SEASON },
                    { name: "majorDivision", in: "query", hint: 'e.g. "ligaen", "2div", "3div"' },
                ],
            },
        ],
    },
    {
        name: "Matches",
        blurb: "Scheduled and played matches.",
        endpoints: [
            {
                id: "matches-team",
                method: "GET",
                path: "/matches",
                title: "A team's played matches",
                description:
                    "Every map a team has played, newest first. Group by matchId to reassemble BO3 series. Leave season empty for the team's full history.",
                params: [
                    { name: "teamId", in: "query", required: true, default: EXAMPLE.teamId, hint: EXAMPLE.teamHint },
                    { name: "season", in: "query", hint: "omit for all seasons" },
                ],
            },
            {
                id: "matches-upcoming",
                method: "GET",
                path: "/matches/upcoming",
                title: "Upcoming matches",
                description: "Scheduled (not yet played) matches, optionally filtered by division or team.",
                params: [
                    { name: "division", in: "query", hint: 'e.g. "ligaen", "2divA"' },
                    { name: "teamId", in: "query", hint: "optional — just this team's fixtures" },
                ],
            },
            {
                id: "match-get",
                method: "GET",
                path: "/matches/{matchId}",
                title: "Played match",
                description:
                    "A played match by ID, returned as one entry per map (scores + veto).",
                params: [{ name: "matchId", in: "path", required: true, default: EXAMPLE.matchId, hint: EXAMPLE.matchHint }],
            },
        ],
    },
    {
        name: "Clips",
        blurb:
            "Generated highlights — multi-kills, clutches and opening duels. clipUrl and thumbnailUrl are absolute and embed straight into a <video> tag. score ranks the highlight; higher is better.",
        endpoints: [
            {
                id: "clips-player",
                method: "GET",
                path: "/clips/player/{steamId}",
                title: "One player's clips",
                description: "A player's clips, newest first. Leave season empty for their whole career.",
                params: [
                    { name: "steamId", in: "path", required: true, default: EXAMPLE.steamId, hint: EXAMPLE.playerHint },
                    { name: "season", in: "query", hint: "omit for all seasons" },
                ],
            },
            {
                id: "clips-team",
                method: "GET",
                path: "/clips/team/{teamId}",
                title: "A team's clips",
                description:
                    "Clips for everyone on a team's roster. Rosters come from live fixture data, so for a past season this is the current roster's clips from that season.",
                params: [
                    { name: "teamId", in: "path", required: true, default: EXAMPLE.teamId, hint: EXAMPLE.teamHint },
                    { name: "season", in: "query", hint: "omit for all seasons" },
                ],
            },
            {
                id: "clips-match",
                method: "GET",
                path: "/clips/match/{matchId}",
                title: "A match's clips",
                description: "Every clip from a played match, across all maps in the series.",
                params: [{ name: "matchId", in: "path", required: true, default: EXAMPLE.matchId, hint: EXAMPLE.matchHint }],
            },
            {
                id: "clips-prematch",
                method: "GET",
                path: "/clips/prematch/{matchId}",
                title: "Prematch build-up reel",
                description:
                    "The best clips from both rosters of an upcoming match, highest score first — built for pre-game segments. Takes the fixture id (the challongeId from Upcoming matches).",
                params: [
                    { name: "matchId", in: "path", required: true, default: EXAMPLE.fixtureId, hint: "fixture id from /matches/upcoming" },
                    { name: "season", in: "query", hint: "optional" },
                    { name: "limit", in: "query", default: "10", hint: "max 100" },
                ],
            },
            {
                id: "clips-top",
                method: "GET",
                path: "/clips/top",
                title: "Top clips of the season",
                description: "The league's highest-scoring clips for a season, best first.",
                params: [
                    { name: "season", in: "query", default: CURRENT_SEASON },
                    { name: "limit", in: "query", default: "10", hint: "max 100" },
                ],
            },
        ],
    },
];

const buildUrl = (ep: Endpoint, vals: Record<string, string>) => {
    let path = ep.path;
    for (const p of ep.params.filter((p) => p.in === "path")) {
        path = path.replace(`{${p.name}}`, encodeURIComponent(vals[p.name] || ""));
    }
    const query = ep.params
        .filter((p) => p.in === "query" && (vals[p.name] ?? "").trim() !== "")
        .map((p) => `${p.name}=${encodeURIComponent(vals[p.name].trim())}`)
        .join("&");
    return `${API_BASE}${path}${query ? "?" + query : ""}`;
};

const EndpointCard: React.FC<{ ep: Endpoint; apiKey: string }> = ({ ep, apiKey }) => {
    const initial = useMemo(() => {
        const o: Record<string, string> = {};
        ep.params.forEach((p) => (o[p.name] = p.default ?? ""));
        return o;
    }, [ep]);

    const [vals, setVals] = useState<Record<string, string>>(initial);
    const [res, setRes] = useState<{ status: number; body: string; ok: boolean } | null>(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const url = buildUrl(ep, vals);
    const curl = `curl -H "X-API-Key: ${apiKey || "YOUR_KEY"}" "${url}"`;

    const run = async () => {
        setLoading(true);
        setRes(null);
        try {
            const r = await fetch(url, { headers: apiKey ? { "X-API-Key": apiKey } : {} });
            let body: string;
            const text = await r.text();
            try {
                body = JSON.stringify(JSON.parse(text), null, 2);
            } catch {
                body = text;
            }
            setRes({ status: r.status, body, ok: r.ok });
        } catch (e: any) {
            setRes({ status: 0, body: `Request failed: ${e?.message || e}`, ok: false });
        }
        setLoading(false);
    };

    const copy = () => {
        navigator.clipboard?.writeText(curl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className={styles.endpoint}>
            <div className={styles.endpointHead}>
                <span className={styles.method}>{ep.method}</span>
                <code className={styles.path}>{ep.path}</code>
                <span className={styles.endpointTitle}>{ep.title}</span>
            </div>
            <p className={styles.endpointDesc}>{ep.description}</p>

            {ep.params.length > 0 && (
                <div className={styles.params}>
                    {ep.params.map((p) => (
                        <label key={p.name} className={styles.paramField}>
                            <span className={styles.paramLabel}>
                                {p.name}
                                {p.in === "path" && <span className={styles.pathTag}>path</span>}
                                {p.required && <span className={styles.reqTag}>required</span>}
                                {!p.required && <span className={styles.optTag}>optional</span>}
                            </span>
                            <input
                                className={styles.paramInput}
                                value={vals[p.name]}
                                placeholder={p.hint || ""}
                                onChange={(e) => setVals((v) => ({ ...v, [p.name]: e.target.value }))}
                            />
                        </label>
                    ))}
                </div>
            )}

            <div className={styles.reqRow}>
                <code className={styles.url}>{url}</code>
            </div>

            <div className={styles.actions}>
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={run} disabled={loading}>
                    {loading ? "Running…" : "Try it"}
                </button>
                <button className={`${styles.btn} ${styles.btnGhost}`} onClick={copy}>
                    {copied ? "Copied!" : "Copy as curl"}
                </button>
            </div>

            {res && (
                <div className={styles.response}>
                    <div className={styles.responseHead}>
                        <span
                            className={`${styles.status} ${res.ok ? styles.statusOk : styles.statusErr}`}
                        >
                            {res.status === 0 ? "ERR" : `HTTP ${res.status}`}
                        </span>
                        {res.status === 401 && (
                            <span className={styles.hint}>
                                Add your API key at the top of the page.
                            </span>
                        )}
                    </div>
                    <pre className={styles.responseBody}>{res.body}</pre>
                </div>
            )}
        </div>
    );
};

const ApiDocsPage: React.FC = () => {
    const KEY_STORAGE = "ps_public_api_key";
    const [apiKey, setApiKey] = useState<string>(() => localStorage.getItem(KEY_STORAGE) || "");

    const saveKey = (v: string) => {
        setApiKey(v);
        if (v) localStorage.setItem(KEY_STORAGE, v);
        else localStorage.removeItem(KEY_STORAGE);
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>PowerStats API</h1>
            <p className={styles.lede}>
                A simple, read-only REST API for pulling PowerLigaen stats — players, teams, rosters,
                standings, matches and highlight clips — straight into casts, overlays and dashboards.
                Responses are clean JSON. Below you can try every endpoint live with your own key.
            </p>

            {/* Getting started */}
            <div className={styles.grid}>
                <div className={styles.infoCard}>
                    <h3>Base URL</h3>
                    <code className={styles.block}>{API_BASE}</code>
                </div>
                <div className={styles.infoCard}>
                    <h3>Authentication</h3>
                    <p>
                        Send your key in the <code>X-API-Key</code> header on every request. No key ={" "}
                        <code>401</code>.
                    </p>
                </div>
                <div className={styles.infoCard}>
                    <h3>Getting a key</h3>
                    <p>
                        Keys are issued by an admin. Ask on{" "}
                        <a href="https://discord.gg/W54Zhj2PD3" target="_blank" rel="noreferrer">
                            Discord
                        </a>{" "}
                        with what you're building.
                    </p>
                </div>
                <div className={styles.infoCard}>
                    <h3>Fair use</h3>
                    <p>
                        Usage is monitored per key. There are no hard limits yet — just be reasonable
                        and cache where you can.
                    </p>
                </div>
            </div>

            {/* Attribution */}
            <div className={styles.attributionCard}>
                <h3>Attribution — required</h3>
                <p>
                    The data is free to use, but you have to credit PowerStats. Pick whichever fits
                    your production:
                </p>
                <ul>
                    <li>
                        Show the <strong>PowerStats logo</strong> on screen wherever the data appears
                        — an overlay, scoreboard, stat graphic or dashboard corner is fine.
                    </li>
                    <li>
                        Or <strong>mention PowerStats out loud</strong> at least once during any
                        segment that uses the data, and periodically across a longer broadcast.
                    </li>
                </ul>
                <p>
                    A visible link to <code>powerstats.dk</code> counts on websites and dashboards.
                    Ask on Discord if you need logo files or aren't sure whether your setup counts —
                    we'd much rather sort it out than revoke a key.
                </p>
            </div>

            {/* Quick example */}
            <h2 className={styles.sectionTitle}>Quick example</h2>
            <p className={styles.muted}>Fetch a single player's season stats:</p>
            <pre className={styles.exampleBlock}>
{`curl -H "X-API-Key: YOUR_KEY" \\
  "${API_BASE}/players/${EXAMPLE.steamId}?season=${CURRENT_SEASON}"

# → { "steamId": "${EXAMPLE.steamId}", "playerName": "Ryxxo",
#     "teamName": "Linx Legacy", "season": ${CURRENT_SEASON}, "gamesPlayed": 8,
#     "rating": 1.36, "kd": 1.52, "adr": 89.97, "kast": 76.99,
#     "rating30": 1.46, "swingPct": 4.62, ... }`}
            </pre>

            {/* Key bar */}
            <div className={styles.keyBar}>
                <div className={styles.keyBarLabel}>
                    Your API key
                    <span className={styles.muted}> — stored only in this browser, sent only to the API</span>
                </div>
                <div className={styles.keyBarRow}>
                    <input
                        className={styles.keyInput}
                        type="text"
                        value={apiKey}
                        placeholder="pls_…"
                        onChange={(e) => saveKey(e.target.value.trim())}
                    />
                    {apiKey && (
                        <button className={`${styles.btn} ${styles.btnGhost}`} onClick={() => saveKey("")}>
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Endpoints */}
            {GROUPS.map((g) => (
                <section key={g.name} className={styles.group}>
                    <h2 className={styles.sectionTitle}>{g.name}</h2>
                    <p className={styles.muted}>{g.blurb}</p>
                    {g.endpoints.map((ep) => (
                        <EndpointCard key={ep.id} ep={ep} apiKey={apiKey} />
                    ))}
                </section>
            ))}

            <p className={styles.footNote}>
                Data covers PowerLigaen only. Player names are in-game names — real names and ages are
                never exposed.
            </p>
        </div>
    );
};

export default ApiDocsPage;
